import { useMutation, useQuery } from "react-query";
import { axiosInstance } from "@src/api/axiosConfig";
import { expertJoinPath } from "./expertApiConfig";
import { axiosPath } from "../axiosPath";
import axios from "axios";

export type CounselingCategory = "매수신청대리인" | "매물컨설팅" | "임장동행" | "중개";
export type SignupExpertParams = {
    phone: string;
    email: string;
    name: string;
    profileFileUrl?: string;
    deviceId?: string;
    osType: string;
    pushToken?: string;
    isAgreedMarketing: boolean;
    partnerCode?: string;
    counselingCategories: CounselingCategory[];
    officeInfo?: {
        name?: string;
        ceo?: string;
        registrationNumber?: string;
        type?: string;
        phone?: string;
        address?: string;
        pnu?: string;
    };
    career?: Record<CounselingCategory, number>;
    qualification?: string; // 자격
    introduction?: string; // 소개
    isAuctionAgent: boolean; // 매수신청대리인 신청여부
    auctionAgentCertFileUrl?: string; 
    bankName: string; // 은행이름
    bankAccount: string; // 계좌번호
};

export type SignupExpertResponse = {
    success: boolean;
    token: {
        accessToken: string;
        refreshToken: string;
    };
    productName: string;
    subscriptionExpiredAt: string;
    authCode: string;
};

export const useJoinExpert = () => {
    const joinExpert = async (params: SignupExpertParams): Promise<SignupExpertResponse> => {
        // 회원가입(전문가) 요청은 토큰 없이 axios 기본 인스턴스로 요청
        const response = await axios.post(expertJoinPath.joinExpert, params, {
            headers: {
                "Content-Type": "application/json",
            },
        });
        return response.data;
    };
    return useMutation({ mutationFn: joinExpert });
};

// 프로필 이미지 업로드
export async function uploadExpertProfileImage(file: File, isTemp: boolean = true): Promise<string> {
    const formData = new FormData();
    formData.append("profile", file);
    formData.append("isTemp", isTemp.toString());
    const res = await axiosInstance.put(expertJoinPath.uploadProfileImage, formData, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data.url;
}

// 전문가 > 본인 추천인 코드 중복 검사
export type matchPartnerResponse = {
    isDupl: boolean;
    message?: string;
};

export const getParnerCode = async (code: string) => {
    const response = await axiosInstance.get(`${expertJoinPath.matchPartner}/${code}/duplicate`);
    return response;
};

export async function getPartnerCodeDuplicated(code: string): Promise<boolean> {
    try {
        const { data } = await axiosInstance.get<matchPartnerResponse>(`${expertJoinPath.matchPartner}/${code}/duplicate`);
        return data.isDupl;
    } catch (error) {
        console.error(error);
        return false;
    }
}

// 공인중개사무소 조회
export type AgentOfficeParams = {
    sggCode: string;
    name: string;
    limit: number;
    cursor?: string | null;
};

export type AgentOfficeItem = {
    agents: string[];
    assistants: string[];
    no: number;
    name: string;
    registrationNumber: string;
    type: string;
    ceo: string;
    address: string;
    phone: string;
    members: string[];
};

export type AgentOfficeListResponse = {
    totalCount: number;
    cursor: string | null;
    list: AgentOfficeItem[];
};

export const getAgentOffices = async (params: AgentOfficeParams): Promise<AgentOfficeListResponse> => {
    const res = await axiosInstance.get<AgentOfficeListResponse>(expertJoinPath.searchOffice, {
        params: {
            ...params,
            limit: params.limit ?? 20,
            cursor: params.cursor ?? undefined, // null이면 쿼리에서 빠짐
        },
    });

    return res.data;
};

export const useAgentOffices = (params: AgentOfficeParams) => {
    return useQuery({
        queryKey: ["agentOffices", params], // 파라미터별 캐시 분리
        queryFn: () => getAgentOffices(params),
        keepPreviousData: true,
        staleTime: 60_000,
    });
};

// 소재지 조회
export type LocationParams = {
    searchText: string;
};

export type LocationItem = {
    _index: string;
    _type: string;
    _score: number;
    _id: number;
    name: string;
    registrationNumber: string;
    ceo: string;
    address: string;
    phone: string;
    members: string[];
};

export type LocationResponse = {
    list: LocationItem[];
    totalCnt: number;
    cursor: string | null;
};

export const getLocation = async (params: LocationParams): Promise<LocationResponse> => {
    const res = await axiosInstance.get<LocationResponse>(`${expertJoinPath.searchLocation}/${params.searchText}`, {
        params: {
            ...params,
        },
    });
    return res.data;
};

export const useSearchLocation = (params: LocationParams) => {
    return useQuery({
        queryKey: ["location", params],
        queryFn: () => getLocation(params),
        keepPreviousData: true,
    });
};

// 전문가 회원 신청 ( 회원가입 플로우 아님 )
export type ExpertApplicationInfo = {
    phone: string;
    profile: string;
    officeInfo?: {
        name?: string;
        ceo?: string;
        registrationNumber?: string;
        type?: string;
        phone?: string;
        address?: string;
        pnu?: string;
    };
    counselingCategories: CounselingCategory[];
    career: Record<CounselingCategory, number>;
    qualification?: string;
    introduction?: string;
    isAuctionAgent: boolean;
    auctionAgentCertFileUrl?: string;
    bankName: string;
    bankAccount: string;
};

export type UserPhoneInfo = {
    phone: string;
};

export const registerExpertInfo = async (body: Omit<ExpertApplicationInfo, "phone">): Promise<ExpertApplicationInfo> => {
    const userRes = await axiosInstance.get<UserPhoneInfo>(axiosPath.userInfoPath);
    const phone = userRes.data.phone;

    const career = Object.fromEntries(
        Object.entries(body.career || {})
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => [key as CounselingCategory, value as number]),
    );

    try {
        const res = await axiosInstance.post(expertJoinPath.applicationExpert, { ...body, phone, career });
        return res.data;
    } catch (err: any) {
        throw new Error(err.response?.data?.message || "전문가 신청 실패");
    }
};

export const getUserPhone = async (): Promise<string> => {
    try {
        const res = await axiosInstance.get<UserPhoneInfo>(axiosPath.userInfoPath);
        return res.data.phone;
    } catch (err: any) {
        console.error("휴대폰 번호 조회 실패", err);
        throw new Error(err.response?.data?.message || "휴대폰 조회 실패");
    }
};
